/* OnlineStatsPanel — the Online-openers view of the Statistics sub-tab.

   Two horizons, because the data behind them has two lifetimes:
     * All-time opener performance from the durable per-template `sent_count` /
       `converted_count`. These exist for CUSTOM openers only — auto-mode
       built-ins carry no template_id, so an auto-mode campaign shows an empty
       leaderboard and leans on the live snapshot instead.
     * A live snapshot of the deliveries working set (what is in flight right
       now). This includes auto-mode, but the worker prunes it as cooldowns
       elapse, so it is a "right now" figure, never a running total.

   Deliberately no period selector: neither source can honour arbitrary windows
   (see the phase-2 note in the memory / ADR 0002). */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import type { OnlineStatsResponse } from '../../api/client';
import { FeatherIcon } from '../FeatherIcon';
import { Card, Kpi, MetricRow, STAT_BASE, ZeroState, fmt, pct, ratio } from '../Statistics/statsKit';

interface Props {
  accountId: string;
}

export function OnlineStatsPanel({ accountId }: Props) {
  const { t } = useTranslation('automatedMessages');
  const [data, setData] = useState<OnlineStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setData(await api.getOnlineMessagesStats(accountId));
    } catch {
      setError(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    void load();
  }, [load]);

  const convRate = data ? ratio(data.all_time_converted, data.all_time_sent) * 100 : 0;

  // All-time sent/replied counters are only tracked for CUSTOM openers (auto-mode
  // built-ins carry no template_id). For auto-mode the numbers aren't available —
  // a "0" there would read as a real zero, so we hide those KPIs entirely.
  const allTimeAvailable = data?.message_source === 'custom';

  // Live snapshot proportions exclude failed (we don't surface failed sends).
  const liveShown = (data?.live_hanging ?? 0) + (data?.live_cooldown ?? 0) + (data?.live_converted ?? 0);

  // Top openers by conversions, then by sends — the best-performing lines first.
  const topOpeners = useMemo(() => {
    if (!data) return [];
    return [...data.openers]
      .filter((o) => o.sent_count > 0)
      .sort((a, b) => b.converted_count - a.converted_count || b.sent_count - a.sent_count)
      .slice(0, 8);
  }, [data]);

  const nothingYet = !data || (!data.exists) || (data.all_time_sent === 0 && data.live_total === 0);

  return (
    <div style={{ ...STAT_BASE, color: 'var(--text)', display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 800, letterSpacing: -0.6 }}>
            {t('onlineStats.title')}
          </h2>
          <p style={{ margin: '6px 0 0', fontSize: 14, fontWeight: 500, color: 'var(--muted)' }}>{t('onlineStats.subtitle')}</p>
        </div>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 650, color: 'var(--faint)', fontFamily: 'var(--mono)' }}>
          <FeatherIcon name="clock" size={14} />
          {t('onlineStats.horizon')}
        </span>
      </div>

      {error ? (
        <div style={{ padding: '48px 0', textAlign: 'center', fontSize: 14, fontWeight: 600, color: 'var(--faint)' }}>
          <FeatherIcon name="alert-triangle" size={22} />
          <div style={{ marginTop: 8 }}>{t('stats.error')}</div>
        </div>
      ) : nothingYet && !loading ? (
        <ZeroState text={`${t('onlineStats.zero.title')} — ${t('onlineStats.zero.body')}`} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18, opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
          {/* Hero KPIs. All-time sent/replied/conversion only when tracked (custom
              openers); "Awaiting reply" is a live figure, always available. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14 }}>
            {allTimeAvailable && (
              <>
                <Kpi icon="send" tone="accent" label={t('onlineStats.kpi.sent')} value={fmt(data?.all_time_sent ?? 0)} sub={t('onlineStats.kpi.sentHint')} />
                <Kpi icon="message-circle" tone="ok" label={t('onlineStats.kpi.replied')} value={fmt(data?.all_time_converted ?? 0)} sub={t('onlineStats.kpi.repliedHint')} />
                <Kpi icon="trending-up" tone="ok" label={t('onlineStats.kpi.convRate')} value={pct(convRate)} sub={t('onlineStats.kpi.convRateHint')} />
              </>
            )}
            <Kpi icon="radio" tone="warn" label={t('onlineStats.kpi.awaiting')} value={fmt(data?.live_hanging ?? 0)} sub={t('onlineStats.kpi.awaitingHint')} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
            {/* Live snapshot */}
            <Card icon="activity" tone="warn" title={t('onlineStats.live.title')} hint={t('onlineStats.live.hint')}>
              <div>
                <MetricRow label={t('onlineStats.live.hanging')} value={data?.live_hanging ?? 0} of={liveShown} tone="accent" />
                <MetricRow label={t('onlineStats.live.cooldown')} value={data?.live_cooldown ?? 0} of={liveShown} tone="warn" />
                <MetricRow label={t('onlineStats.live.converted')} value={data?.live_converted ?? 0} of={liveShown} tone="ok" />
              </div>
            </Card>

            {/* Opener leaderboard (custom openers only) */}
            <Card icon="award" title={t('onlineStats.openers.title')} hint={t('onlineStats.openers.hint')}>
              {topOpeners.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {/* header row */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 52px 52px 54px', gap: 10, padding: '0 0 8px', fontSize: 10.5, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--faint)' }}>
                    <span>{t('onlineStats.openers.opener')}</span>
                    <span style={{ textAlign: 'right' }}>{t('onlineStats.openers.sent')}</span>
                    <span style={{ textAlign: 'right' }}>{t('onlineStats.openers.replied')}</span>
                    <span style={{ textAlign: 'right' }}>{t('onlineStats.openers.rate')}</span>
                  </div>
                  {topOpeners.map((o) => {
                    const rate = ratio(o.converted_count, o.sent_count) * 100;
                    return (
                      <div key={o.id} style={{ display: 'grid', gridTemplateColumns: '1fr 52px 52px 54px', gap: 10, alignItems: 'center', padding: '9px 0', borderTop: '1px solid var(--line-2)' }}>
                        <span title={o.text} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, fontWeight: 550, color: 'var(--text)' }}>
                          {o.text}
                        </span>
                        <span style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 13.5, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>{fmt(o.sent_count)}</span>
                        <span style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 13.5, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{fmt(o.converted_count)}</span>
                        <span style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 13.5, fontWeight: 700, color: rate > 0 ? 'var(--ok-ico)' : 'var(--faint)', fontVariantNumeric: 'tabular-nums' }}>{pct(rate)}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ margin: 0, fontSize: 13.5, fontWeight: 550, color: 'var(--faint)', lineHeight: 1.5 }}>
                  {data?.message_source === 'auto' ? t('onlineStats.openers.autoNote') : t('onlineStats.openers.empty')}
                </p>
              )}
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
